import { BackendError, BasketCustomerOrderEntity, Service } from '@genial/services';
import { ResponsesBasketCustomerOfferOrderResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { selector, selectorFamily } from 'recoil';

import {
  BasketAdvisorListResponseProps,
  BasketCustomerOrderItemResumeData,
} from '../../../pages/BatchTicket/utils/types';
import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { masterStateSelectedAdvisorSelector } from '../../master/selector';
import { basketAdvisorOrderIdState, basketCustomerOrderResumeState } from '../atom';
import { customerParams } from '../constants';
import {
  filterCustomersWithErrorFreeOffers,
  filterCustomersWithOffersThatNeedAttention,
  normalizeBasketAdvisorListData,
} from '../normalize';

export const basketAdvisorListQuery = selectorFamily<
  BasketAdvisorListResponseProps | null | undefined,
  { page: number }
>({
  key: 'basketAdvisorListQuery',
  get: ({ page }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(masterStateSelectedAdvisorSelector);
        const params = { ...customerParams, page, advisorId: advisorSelected };

        const { data, meta } = await Service.Basket.V1.getBasketAdvisor(params);

        const normalizedData = normalizeBasketAdvisorListData(data);

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const updateBasketAdvisorOrderIdSelector = selector({
  key: 'updateBasketAdvisorOrderIdSelector',
  get: ({ get }) => get(basketAdvisorOrderIdState),
  set: ({ set }, basketAdvisorOrderId) => {
    set(basketAdvisorOrderIdState, basketAdvisorOrderId as unknown as string);
  },
});

export const removeBasketCustomerOrderItemResumeSelector = selector({
  key: 'removeBasketCustomerOrderItemResumeSelector',
  set: ({ set, get }, basketCustomerOrderId) => {
    const basketCustomerOrderResume = get(basketCustomerOrderResumeState);
    const updatedBasketCustomerOrderResume = basketCustomerOrderResume.data.all.filter(
      (basketCustomerItem) => basketCustomerItem.basketCustomerOrderId !== basketCustomerOrderId
    );
    set(basketCustomerOrderResumeState, {
      ...basketCustomerOrderResume,
      data: {
        all: updatedBasketCustomerOrderResume,
        errors: filterCustomersWithOffersThatNeedAttention(updatedBasketCustomerOrderResume),
        valid: filterCustomersWithErrorFreeOffers(updatedBasketCustomerOrderResume),
      },
    });
  },
  get: ({ get }) => get(basketCustomerOrderResumeState),
});

export const addBasketCustomerOrderItemResumeSelector = selector({
  key: 'addBasketCustomerOrderItemResumeSelector',
  set: ({ set }, customerOrderResume) => {
    const data = customerOrderResume as unknown as BasketCustomerOrderItemResumeData;
    set(basketCustomerOrderResumeState, {
      summary: data.summary,
      data: {
        all: data.data,
        errors: filterCustomersWithOffersThatNeedAttention(data.data),
        valid: filterCustomersWithErrorFreeOffers(data.data),
      },
    });
  },
  get: ({ get }) => get(basketCustomerOrderResumeState),
});

export const updateBasketCustomerOrderItemResumeSelector = selector({
  key: 'updateBasketCustomerOrderItemResumeSelector',
  set: ({ set, get }, customerOrderResume) => {
    const customerOrderItem = customerOrderResume as unknown as BasketCustomerOrderEntity;
    const basketCustomerOrderResume = get(basketCustomerOrderResumeState);

    const updatedBasketCustomerOrderResume = basketCustomerOrderResume.data.all.map((item) => {
      if (item.basketCustomerOrderId === customerOrderItem.basketCustomerOrderId) {
        return customerOrderItem;
      }
      return item;
    });

    set(basketCustomerOrderResumeState, {
      summary: basketCustomerOrderResume.summary,
      data: {
        all: updatedBasketCustomerOrderResume,
        errors: filterCustomersWithOffersThatNeedAttention(updatedBasketCustomerOrderResume),
        valid: filterCustomersWithErrorFreeOffers(updatedBasketCustomerOrderResume),
      },
    });
  },

  get: ({ get }) => get(basketCustomerOrderResumeState),
});

export const updateBasketCustomerOrderCoeOfferItemResumeSelector = selector({
  key: 'updateBasketCustomerOrderCoeOfferItemResumeSelector',
  set: ({ set, get }, customerOrderOfferResume) => {
    const customerOrderOfferItem =
      customerOrderOfferResume as unknown as ResponsesBasketCustomerOfferOrderResponse;
    const basketCustomerOrderResume = get(basketCustomerOrderResumeState);

    const updatedBasketCustomerOrderOfferResume = basketCustomerOrderResume.data.all.map(
      (basketCustomerOffer) => {
        const isCustomerOrderToChange =
          basketCustomerOffer.basketCustomerOrderId ===
          customerOrderOfferItem.basketOffer.basketCustomerOrderId;

        if (isCustomerOrderToChange) {
          const basketCustomerOffers = basketCustomerOffer.customerOffers?.map((customerOffer) => {
            const marketIsCOE = customerOffer.marketType === 'COE';

            if (marketIsCOE) {
              const basketOffers = customerOffer.basketOffers?.map((basketOffer) => {
                const isOfferToChange =
                  basketOffer.basketCustomerOfferId ===
                  customerOrderOfferItem.basketOffer.basketCustomerOfferId;

                if (isOfferToChange) {
                  return { ...customerOrderOfferItem.basketOffer };
                }

                return { ...basketOffer };
              });

              return { ...customerOffer, basketOffers };
            }

            return { ...customerOffer };
          });

          return { ...basketCustomerOffer, customerOffers: basketCustomerOffers };
        }

        return basketCustomerOffer;
      }
    );

    set(basketCustomerOrderResumeState, {
      summary: basketCustomerOrderResume.summary,
      data: {
        all: updatedBasketCustomerOrderOfferResume,
        errors: filterCustomersWithOffersThatNeedAttention(updatedBasketCustomerOrderOfferResume),
        valid: filterCustomersWithErrorFreeOffers(updatedBasketCustomerOrderOfferResume),
      },
    });
  },

  get: ({ get }) => get(basketCustomerOrderResumeState),
});

export const totalCustomerBasketOrderResumeSelector = selector({
  key: 'totalCustomerBasketOrderResumeSelector',
  get: ({ get }) => {
    const validData = get(basketCustomerOrderResumeState).data.valid;
    return validData.length;
  },
});

export const totalOffersBasketOrderResumeSelector = selector({
  key: 'totalOffersBasketOrderResumeSelector',
  get: ({ get }) => {
    const validData = get(basketCustomerOrderResumeState).data.valid;

    const totalOffersOrder = validData?.reduce((acc, current) => {
      const amountOffersByBasket =
        current?.customerOffers?.reduce((accOffersByBasket, currentOffersByBasket) => {
          return accOffersByBasket + (currentOffersByBasket?.basketOffers?.length || 0);
        }, 0) || 0;

      return acc + amountOffersByBasket;
    }, 0);

    return totalOffersOrder;
  },
});

export const totalOffersByMarketBasketOrderResumeSelector = selector({
  key: 'totalOffersByMarketBasketOrderResumeSelector',
  get: ({ get }) => {
    const validData = get(basketCustomerOrderResumeState).data.valid;

    const OffersByMarketBasketOrder = validData.reduce((acc, item) => {
      item?.customerOffers?.forEach((offer) => {
        if (offer?.marketType && !acc.includes(offer?.marketType)) {
          acc.push(offer?.marketType);
        }
      });

      return acc;
    }, [] as string[]);

    const totalOffersByMarketBasketOrder = OffersByMarketBasketOrder.length;

    return totalOffersByMarketBasketOrder;
  },
});

export const totalAmountBasketOffersResumeSelector = selector({
  key: 'totalAmountBasketOffersResumeSelector',
  get: ({ get }) => {
    const validData = get(basketCustomerOrderResumeState).data.valid;

    const totalAmount = validData.reduce((accumulator, item) => {
      const customerOffersSum =
        item?.customerOffers?.reduce((subAccumulator, customerOffer) => {
          const basketOffersSum =
            customerOffer.basketOffers?.reduce((offerAcc, basketOffer) => {
              return offerAcc + basketOffer.amount;
            }, 0) || 0;

          return subAccumulator + basketOffersSum;
        }, 0) || 0;

      return accumulator + customerOffersSum;
    }, 0);

    return { value: totalAmount, formatted: Utils.Mask.Money(totalAmount) };
  },
});

export const totalAvailableBalanceBasketOrderResumeSelector = selector({
  key: 'totalAvailableBalanceBasketOrderResumeSelector',
  get: ({ get }) => {
    const allData = get(basketCustomerOrderResumeState).data.all;

    const totalAvailableBalance = allData?.reduce((acc, current) => {
      const currentAvailableBalance = current?.customer?.account?.availableBalance || 0;
      return acc + currentAvailableBalance;
    }, 0);

    return { value: totalAvailableBalance, formatted: Utils.Mask.Money(totalAvailableBalance) };
  },
});
